import { AxiosResponse } from 'axios';
import { ERROR_MESSAGES } from 'common/constants';
import { PaginatedApiPayload } from 'domain/Common';
import { get, isArray, isString, mapValues, snakeCase } from 'lodash-es';

/**
 * Transforms a paginated API payload to ensure backwards compatibility.
 * [NAT-4275](https://natterco.atlassian.net/browse/NAT-4275)
 *
 * @template T - The type of data in the payload.
 * @param {PaginatedApiPayload<T>} response - The paginated API response to transform.
 * @returns {PaginatedApiPayload<T>} The transformed paginated API response.
 */
export const transformPaginatedApiPayload = <T = unknown>(response: PaginatedApiPayload<T>): PaginatedApiPayload<T> => {
  if (!response) {
    return response;
  }

  const transformedResponse: PaginatedApiPayload<T> = { ...response };

  if (response.content) {
    transformedResponse.data = response.content;
  }

  return transformedResponse;
};

type WithData = {
  data: unknown;
};

type WithValidationErrors = {
  validationErrors: Record<string, string[]>;
};

type WithErrors = {
  errors: { message: string; errorId: string; stackTrace: string; metadata: unknown }[];
};

type ValidationError = {
  data: {
    validationErrors: Record<string, string[]>;
  };
};

export const hasValidationErrors = (obj: unknown): obj is WithValidationErrors =>
  (obj as WithValidationErrors)?.validationErrors !== undefined;

export const hasErrors = (obj: unknown): obj is WithErrors => (obj as WithErrors)?.errors !== undefined;

export const hasData = (obj: unknown): obj is WithData => (obj as WithData)?.data !== undefined;

export const isValidationError = (obj: unknown): obj is ValidationError =>
  hasData(obj) && hasValidationErrors(obj.data);

/** The API requires arrays to be joined by comma, and has new pagination request so this mapper converts old request params into new one
 *
 * @example
 * ```
 * // old
 * {
 *   sortOrder: 'asc' | 'desc',
 *   sortBy: 'displayName',
 * }
 * // new
 * {
 *   sortDir: 'ASC' | 'DESC',
 *   sortParam: 'DISPLAY_NAME',
 *   tagIds: 123,456
 * }
 * ```
 */
export const mapToApiParams = <P extends Record<string, unknown>>(params: P) =>
  mapValues(params, (value, key) => {
    if (key === 'sortParam' && typeof value === 'string') return snakeCase(value).toUpperCase();
    if (key === 'sortDir' && typeof value === 'string') return value.toUpperCase();
    if (isArray(value)) return value.join(',');
    return value;
  });

/**
 * Machine-readable error codes the backend surfaces via `metadata.errorType` so the client can
 * branch on the specific failure rather than parsing the human-readable message.
 */
export type ApiErrorType =
  | 'VALIDATION_ERROR'
  | 'NOT_COMMUNITY_MEMBER'
  | 'EVENT_NOT_OPEN_FOR_REGISTRATION'
  | 'EVENT_NOT_JOINABLE'
  /** A Natalie event has spent its join ceiling, so no further conversation can start. */
  | 'JOIN_LIMIT_REACHED'
  /** A Natalie event is expired or otherwise not running. */
  | 'EVENT_NOT_ACTIVE'
  /** A 404. In a room's first seconds it can mean the room is not projected yet. */
  | 'RESOURCE_NOT_FOUND'
  /** A 409 with no more specific code, e.g. a client-minted id that is already in use. */
  | 'CONFLICT'
  /** The topic's edit window has closed, so its perspectives can no longer change. */
  | 'TOPIC_ROUND_CLOSED'
  /** A report already has a regeneration or quotes job running; only one runs at a time. */
  | 'REPORT_JOB_RUNNING'
  /** The report has not finished its first generation, so there is nothing to change yet. */
  | 'REPORT_NOT_GENERATED'
  /** Some of the report's transcripts are still being processed. */
  | 'TRANSCRIPTS_IN_PROGRESS'
  /** None of the report's events has a text question to generate from. */
  | 'REPORT_NOTHING_TO_GENERATE';

export type ApiExceptionMessage = {
  errorId: string;
  message: string;
  stackTrace?: string; // only on local and dev
  metadata?: {
    fieldName?: string;
    fieldValue?: string;
    errorType?: ApiErrorType;
    /** Present on NOT_COMMUNITY_MEMBER so the client knows which community to join. */
    communityId?: number;
  };
};

export type ApiErrors = {
  errors: ApiExceptionMessage[];
};

/**
 * Reads the first error's `metadata` from a (RTK Query / axios) error. Handles both the raw
 * response body (`{ errors: [...] }`) and the RTK Query wrapper (`{ status, data }`).
 */
export const getApiErrorMetadata = (error: unknown): ApiExceptionMessage['metadata'] | undefined => {
  const data = error && typeof error === 'object' && 'data' in error ? (error as { data: unknown }).data : error;
  return isApiErrors(data) ? data.errors[0]?.metadata : undefined;
};

export const getErrorMessagesFromResponse = (
  // TODO: add type guards for errorResponse

  errorResponse?: AxiosResponse,
  defaultErrorMessage = ERROR_MESSAGES.unknownError
): string[] => {
  const errors: string[] = [];
  const validationErrors = isString(errorResponse) ? errorResponse : get(errorResponse, 'data.validationErrors', {});

  Object.keys(validationErrors).forEach((key) => {
    const errorMessages = validationErrors[key];
    if (Array.isArray(errorMessages) && errorMessages.length) {
      errorMessages.forEach((errorMessage) => {
        errors.push(errorMessage);
      });
    }
  });

  if (errors.length === 0 && errorResponse?.data) {
    errors.push(errorResponse.data);
  }

  if (errors.length === 0) {
    errors.push(defaultErrorMessage);
  }

  return errors;
};

export const isApiResponse = (response: unknown): response is Pick<AxiosResponse, 'data' | 'status'> =>
  !!response && typeof response === 'object' && 'data' in response && 'status' in response;

export const isApiErrorMessage = (error: unknown): error is ApiExceptionMessage =>
  !!error && typeof error === 'object' && 'errorId' in error && 'message' in error;

export const isApiErrors = (error: unknown): error is ApiErrors =>
  !!error &&
  typeof error === 'object' &&
  'errors' in error &&
  isArray(error.errors) &&
  error.errors.every(isApiErrorMessage);
